import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import window from 'global/window';
import { matchPath } from 'react-router';
import { init } from '@contentful/app-sdk';
import document from 'global/document';
import loadable from '@loadable/component';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { generateStore } from './app/store/store';
import {
  PRELOADED_STATE_KEY, AWS_ENV_VARS, ROUTER_BASENAME, MAINTENANCE_MODE, ENVIRONMENT,
  ADSK_LAUNCH_DARKLY_CLIENTSIDE_KEY,
} from './app/common/constants';
import ClientComponentLoader from './app/common/clientComponentLoader';
import { EnvProvider } from './app/common/envContext';
import logger from './app/common/logger';

import { defaultRoutes, maintenanceRoutes } from './app/common/routerConfig';
import { getEnvironmentConstant } from './app/common/utilities';

// TODO: Remove this console error suppression once @adsk/identity-web-sdk is updated
// Current SDK version: @adsk/identity-web-sdk@^2.1.1
// This suppresses known CORS warnings from the SDK's iframe implementation
// The warnings are cosmetic - the SDK functions correctly despite them
// Issue: The SDK's iframe makes cross-origin requests that trigger browser security warnings
// Impact: Development console noise only - no functional impact
// Action: Remove this workaround when SDK is updated to fix the underlying issue
if (process.env.NODE_ENV === 'development') {
  const originalError = console.error;
  console.error = (...args) => {
    if (
      typeof args[0] === 'string'
      && (args[0].includes('Blocked a frame with origin')
        || args[0].includes('cross-origin frame')
        || args[0].includes('SecurityError'))
    ) {
      // Suppress SDK iframe CORS warnings - they're expected and don't affect functionality
      return;
    }
    originalError.call(console, ...args);
  };
}

const App = loadable(() => import(/* webpackChunkName: "App" */'./App'));

let preloadedState = {};
// Grab the state from a global variable injected into the server-generated HTML
// eslint-disable-next-line no-underscore-dangle
preloadedState = window[PRELOADED_STATE_KEY];
const defaultEnvData = {};
Object.keys(AWS_ENV_VARS).forEach((key) => {
  defaultEnvData[key] = AWS_ENV_VARS[key].default;
});
if (process.env.REACT_APP_ENVIRONMENT) {
  defaultEnvData[ENVIRONMENT] = process.env.REACT_APP_ENVIRONMENT;
}
const env = preloadedState?.envData || defaultEnvData;
env.COMPONENT_LOADER = ClientComponentLoader;

// Allow this to be dynamic from env props
// eslint-disable-next-line camelcase,no-undef
__webpack_public_path__ = `${getEnvironmentConstant({ key: ROUTER_BASENAME, env: env[ENVIRONMENT] })}/`;

logger.info('initializing app');

// Create Redux store with initial state
const store = generateStore(preloadedState);

const routes = env[MAINTENANCE_MODE] ? maintenanceRoutes : defaultRoutes;

const hydrateApplication = async () => {
  const adskLDClientSideId = `${getEnvironmentConstant({ key: ADSK_LAUNCH_DARKLY_CLIENTSIDE_KEY, env: env[ENVIRONMENT] })}`;
  const LDProvider = await asyncWithLDProvider({
    clientSideID: adskLDClientSideId,
  });

  // document.getElementById('root').style.minHeight = `${document.getElementById('root').offsetHeight}px`;
  const root = createRoot(document.getElementById('root'));
  root.render(
    <LDProvider>
      <HelmetProvider>
        <Provider store={store}>
          <BrowserRouter basename={getEnvironmentConstant({ key: ROUTER_BASENAME, env: env[ENVIRONMENT] })}>
            <EnvProvider value={env}>
              <App />
            </EnvProvider>
          </BrowserRouter>
        </Provider>
      </HelmetProvider>
    </LDProvider>,
  );
};

if (document.location.pathname === '/app/problemEditor') {
  const filtered = routes.filter((route) => matchPath(document.location.pathname, {
    path: route.path,
    exact: route.exact || false,
    strict: route.strict || false,
  }) !== null);

  if (filtered.length) {
    new Promise(((resolve) => {
      init((sdk) => { resolve(sdk); });
    })).then((sdk) => {
      ClientComponentLoader[filtered[0].componentKey].load().then(() => {
        const AppComponent = ClientComponentLoader[filtered[0].componentKey];
        const root = createRoot(document.getElementById('root'));
        root.render(
          <AppComponent sdk={sdk} />,
        );
      });
    });
  } else {
    hydrateApplication();
  }
} else {
  const filtered = routes.filter((route) => matchPath(document.location.pathname, {
    path: getEnvironmentConstant({ key: ROUTER_BASENAME, env: env[ENVIRONMENT] }) + route.path,
    exact: route.exact || false,
    strict: route.strict || false,
  }) !== null);

  // console.warn('Filtered: ', filtered);

  if (filtered.length) {
    // Found a matching component for the route.  Wait for it to load before attempting hydration

    logger.info('Preloading component: ', filtered[0].componentKey);
    ClientComponentLoader[filtered[0].componentKey].load().then(() => {
      hydrateApplication();
    });
  } else {
    hydrateApplication();
  }
}
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.unregister();
